// Notification service — API_INTEGRATION.md §3.18
// In-app notification management: list, mark-read, unread count,
// channel preferences, and G2.12 Web Push subscriptions.
// Vertical: SHARED. No feature flag.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
// G2.9: canonical notification enums shared with POS + backend routing.
import type {
  NotificationType,
  NotificationPriority,
  BroadcastScope,
  BroadcastLogEntry,
  CreateBroadcastRequest,
  CreateTenantAnnouncementRequest,
  BroadcastSendResponse,
} from '@bbpos/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export type {
  NotificationType,
  NotificationPriority,
  BroadcastScope,
  BroadcastLogEntry,
  CreateBroadcastRequest,
  CreateTenantAnnouncementRequest,
  BroadcastSendResponse,
};

/** Canonical channel identifier — matches backend MATRIX_CHANNEL_COLUMNS. */
export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'whatsapp' | 'push';

/** Per-(type, user) row from GET /notifications/preferences/matrix. */
export interface NotificationPreferenceMatrixRow {
  notification_type: string;
  in_app: boolean;
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  push: boolean;
  updated_at: string;
}

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  is_read: boolean;
  action_url: string | null;
  /** The event's `data` JSONB, as the API returns it (deep links, ids, …). */
  metadata?: Record<string, unknown> | null;
  created_at: string;
  /** Only on GET /notifications/:id. */
  read_at?: string | null;
}

export interface NotificationListParams {
  page?: number;
  limit?: number;
  type?: string;
  is_read?: boolean;
}

export interface BroadcastListParams {
  page?: number;
  limit?: number;
}

/** Dataset-scoped Broadcast Center KPI aggregates (full history). */
export interface BroadcastStats {
  sent_today: number;
  sent_last_7_days: number;
  total_reach: number;
}

/** Flat per-channel master switches. Mirrors POS copy. */
export interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  push_enabled: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const notificationService = {
  async list(params: NotificationListParams = {}): Promise<{
    data: Notification[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      type: params.type,
      is_read:
        params.is_read === undefined
          ? undefined
          : params.is_read
            ? 'true'
            : 'false',
    });
    return apiClientPaginated<Notification[]>(`/notifications${q}`);
  },

  /** One notification for the detail view (T24); 404 when it isn't the caller's to see. */
  async get(id: string): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}`);
  },

  async getUnreadCount(): Promise<number> {
    const result = await apiClient<{ unread_count: number }>('/notifications/unread-count');
    return (result as unknown as { unread_count: number }).unread_count ?? 0;
  },

  async markAsRead(id: string): Promise<void> {
    await apiClient<void>(`/notifications/${id}/read`, { method: 'PATCH' });
  },

  async markAllAsRead(): Promise<void> {
    await apiClient<void>('/notifications/read-all', { method: 'PATCH' });
  },

  // ─── Channel preferences (flat master switches) ─────────────────────────

  async getPreferences(): Promise<NotificationPreferences> {
    return apiClient<NotificationPreferences>('/notifications/preferences');
  },

  async updatePreferences(
    prefs: Partial<NotificationPreferences>,
  ): Promise<NotificationPreferences> {
    return apiClient<NotificationPreferences>('/notifications/preferences', {
      method: 'PATCH',
      body: prefs,
    });
  },

  // ─── G2.12 — Web Push Subscriptions ──────────────────────────────────────

  /**
   * Returns the environment's VAPID public key, or `null` if Web Push is
   * not configured on the server. The frontend uses this key to call
   * `PushManager.subscribe()`.
   */
  async getVapidPublicKey(): Promise<{ public_key: string | null }> {
    return apiClient<{ public_key: string | null }>(
      '/notifications/push/vapid-public-key',
    );
  },

  /**
   * Persist a browser push subscription on the backend. Idempotent on
   * (user, endpoint) — repeated calls refresh the keys + last_seen_at.
   */
  async registerPushSubscription(
    subscription: PushSubscriptionJSON,
  ): Promise<{ id: string; endpoint: string; user_agent: string | null }> {
    return apiClient<{ id: string; endpoint: string; user_agent: string | null }>(
      '/notifications/push/subscriptions',
      { method: 'POST', body: subscription },
    );
  },

  /** Remove a subscription by its endpoint URL. Idempotent. */
  async unregisterPushSubscription(endpoint: string): Promise<{ removed: number }> {
    return apiClient<{ removed: number }>(
      '/notifications/push/subscriptions',
      { method: 'DELETE', body: { endpoint } },
    );
  },

  // ─── G2.10 — Per-type channel matrix (MANAGER+) ─────────────────────────

  async getMatrix(): Promise<{ data: NotificationPreferenceMatrixRow[] }> {
    return apiClient<{ data: NotificationPreferenceMatrixRow[] }>(
      '/notifications/preferences/matrix',
    );
  },

  async updateMatrixCell(payload: {
    notification_type: string;
    channel: NotificationChannel;
    enabled: boolean;
  }): Promise<NotificationPreferenceMatrixRow> {
    return apiClient<NotificationPreferenceMatrixRow>(
      '/notifications/preferences/matrix',
      { method: 'PATCH', body: payload },
    );
  },

  async resetMatrix(): Promise<{ removed: number }> {
    return apiClient<{ removed: number }>(
      '/notifications/preferences/matrix',
      { method: 'DELETE' },
    );
  },

  // ─── G3.6 — Broadcast Center ────────────────────────────────────────────

  async sendBroadcast(payload: CreateBroadcastRequest): Promise<BroadcastSendResponse> {
    return apiClient<BroadcastSendResponse>('/notifications/broadcast', {
      method: 'POST',
      body: payload,
    });
  },

  async sendTenantAnnouncement(
    payload: CreateTenantAnnouncementRequest,
  ): Promise<BroadcastSendResponse> {
    return apiClient<BroadcastSendResponse>('/notifications/tenant-announce', {
      method: 'POST',
      body: payload,
    });
  },

  async listBroadcasts(params: BroadcastListParams = {}): Promise<{
    data: BroadcastLogEntry[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
    });
    return apiClientPaginated<BroadcastLogEntry[]>(`/notifications/broadcasts${q}`);
  },

  /**
   * Dataset-scoped Broadcast Center KPIs — Sent today / Last 7 days /
   * Estimated reach aggregated server-side over the FULL history, so the
   * cards stay correct once history exceeds one page.
   */
  async getBroadcastStats(): Promise<BroadcastStats> {
    return apiClient<BroadcastStats>('/notifications/broadcasts/stats');
  },
};

export { ApiError };
